import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogIn, Bot, Eye, EyeOff, ArrowLeft, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { QHubBuddyLogo } from "@/components/QHubBranding";
import { NetworkGraph } from "@/components/NetworkGraph";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [heroHoveredNode, setHeroHoveredNode] = useState(null);
  const [coldStart, setColdStart] = useState(false);

  const handleHeroNodeAction = () => {
    document.getElementById("login-form-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    // Cold-start hint: if the server takes >3s to respond (typical when the
    // backend pod is scaling from zero) show a soft "Cold start" badge so
    // teachers/students know it isn't an error, just a warm-up.
    const coldStartTimer = setTimeout(() => setColdStart(true), 3000);
    try {
      setLoading(true);
      setColdStart(false);
      setError("");
      // Si no contiene '@', es un nombre de usuario genérico
      const isUsername = !email.includes("@");
      const payload = isUsername
        ? { username: email.trim(), password }
        : { email: email.trim().toLowerCase(), password };
      const ident = isUsername ? payload.username : payload.email;

      // Retry with exponential backoff on transient network / 429 errors.
      // Fixes the "Failed to fetch" recurrence when several students in a
      // computer lab hit /api/auth/login-password simultaneously.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/login-password`;
      const doLogin = async () => fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Custom header used by backend to key rate-limit per-user (see auth.py::_auth_key)
          "X-Login-Identifier": ident || "",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const MAX_ATTEMPTS = 3;
      let lastErr = null;
      let res = null;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        try {
          res = await doLogin();
          // Retry only for 429 (rate limited) and 5xx (transient server errors)
          if (res.ok || (res.status >= 400 && res.status < 500 && res.status !== 429)) break;
        } catch (netErr) {
          // TypeError: Failed to fetch → transient, retry
          lastErr = netErr;
        }
        if (attempt < MAX_ATTEMPTS) {
          const backoffMs = 500 * Math.pow(2, attempt - 1); // 500ms, 1s, 2s
          setError(`Servidor ocupado. Reintentando (${attempt}/${MAX_ATTEMPTS - 1})…`);
          await new Promise((r) => setTimeout(r, backoffMs));
        }
      }
      if (!res) {
        // All attempts threw at network layer
        throw new Error("No se pudo conectar con el servidor. Revisa tu conexión e intenta de nuevo.");
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 429) {
          throw new Error("Hay muchos intentos en tu red. Espera unos segundos e intenta de nuevo.");
        }
        throw new Error(data.detail || "Credenciales inválidas");
      }
      setError(""); // clear "reintentando" message on success
      // Determine landing page by role (avoids teachers w/o grade being
      // bounced to /select-grade by Dashboard.jsx).
      try {
        const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: "include",
        });
        const me = meRes.ok ? await meRes.json() : null;
        const role = me?.role;
        const ROLE_HOMES = {
          super_admin: "/super-admin",
          institutional_admin: "/institution-admin",
          teacher: "/teacher",
          parent: "/parent",
        };
        // Fase 9-fix: estudiantes B2C aterrizan directo en el módulo AVANZO.
        // Probe barato: /api/avanzo-b2c/access devuelve 200 solo si el user
        // pertenece al school B2C y tiene subscription activa.
        let target = ROLE_HOMES[role] || "/dashboard";
        if (role === "parent") {
          // Padres B2C: el progreso de Buddy AVANZO vive en /avanzo/familia.
          try {
            const accessRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/avanzo-b2c/access`,
              { credentials: "include" },
            );
            if (accessRes.ok) {
              const access = await accessRes.json();
              if (access?.allowed && access?.access_type === "b2c") target = "/avanzo/familia";
            }
          } catch { /* silencioso — si falla, cae a /parent */ }
        }
        if (role === "student") {
          try {
            const accessRes = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/avanzo-b2c/access`,
              { credentials: "include" },
            );
            if (accessRes.ok) {
              const access = await accessRes.json();
              if (access?.allowed) target = "/avanzo";
            }
          } catch { /* silencioso — si falla, cae al dashboard */ }
        }
        window.location.href = target;
      } catch {
        window.location.href = "/dashboard";
      }
    } catch (e) {
      setError(e.message || "No se pudo iniciar sesión.");
    } finally {
      clearTimeout(coldStartTimer);
      setColdStart(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 to-white" data-testid="login-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <QHubBuddyLogo size="default" />
            </Link>
            <Link to="/" className="text-sm font-medium text-gray-600 hover:text-[#419cca] transition-colors flex items-center gap-1" data-testid="back-to-landing">
              <ArrowLeft className="w-4 h-4" /> Volver al inicio
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col pt-16">
        {/* ═══ RED NEURONAL HERO ═══ */}
        <section className="py-16 px-4 bg-gradient-to-b from-slate-50 to-white" data-testid="login-hero-section">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-6">
                <span className="px-3 py-1 rounded-full bg-[#419cca]/10 border border-[#419cca]/20 text-[#419cca] text-sm font-medium">IA Educativa</span>
                <span className="px-3 py-1 rounded-full bg-[#8b5cf6]/10 border border-[#8b5cf6]/20 text-[#8b5cf6] text-sm font-medium">Adaptativa</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-gray-900 leading-tight mb-4" data-testid="login-hero-title">
                Red Neuronal de
                <span className="block bg-gradient-to-r from-[#419cca] to-[#8b5cf6] bg-clip-text text-transparent">Aprendizaje</span>
              </h1>
              <p className="text-gray-500 text-lg mb-8 leading-relaxed max-w-lg" data-testid="login-hero-subtitle">
                Un modelo educativo adaptativo impulsado por inteligencia artificial que guía, personaliza y evidencia el desarrollo cognitivo de cada estudiante.
              </p>
              <div className="flex items-center gap-4">
                <a href="#login-form-section">
                  <Button className="h-12 px-8 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white font-medium text-base hover:opacity-90" data-testid="login-hero-cta">
                    Comenzar ahora
                  </Button>
                </a>
                <Link to="/" className="text-[#419cca] font-medium text-sm hover:underline flex items-center gap-1" data-testid="login-hero-learn-more">
                  Conocer más <ChevronDown className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <NetworkGraph
                onNodeTutor={handleHeroNodeAction}
                hoveredNode={heroHoveredNode}
                setHoveredNode={setHeroHoveredNode}
                testIdPrefix="login-network"
                height={360}
                showLabels={false}
                showTooltipOnHover={false}
              />
            </div>
          </div>
        </section>

        {/* ═══ LOGIN FORM ═══ */}
        <div id="login-form-section" className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-200/50">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shadow-lg shadow-[#419cca]/20">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold font-['Outfit'] text-gray-900 mb-2">
                Ingresar a QHUB.BUDDY
              </h2>
              <p className="text-gray-500 text-sm">
                Inicia sesión con tu cuenta
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Correo electrónico o usuario"
                type="text"
                className="h-12 rounded-xl"
                data-testid="login-email"
                autoComplete="username"
              />
              <div className="relative">
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  type={showPassword ? "text" : "password"}
                  className="h-12 rounded-xl pr-10"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-visibility-button"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg p-2.5" data-testid="login-error">{error}</p>
              )}

              <div className="flex justify-end -mt-1">
                <Link
                  to="/buddy-avanza/forgot-password"
                  className="text-xs font-medium text-[#419cca] hover:text-[#8b5cf6] hover:underline"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>

              {coldStart && loading && !error && (
                <div
                  className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5"
                  data-testid="login-cold-start"
                >
                  <span className="inline-flex w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  <span className="font-semibold uppercase tracking-wide">Cold start</span>
                  <span className="text-amber-600">— warming up server…</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:opacity-90 text-white font-medium text-base flex items-center justify-center gap-2"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Ingresando...</>
                ) : (
                  <><LogIn className="w-4 h-4" /> Iniciar Sesión</>
                )}
              </Button>
            </form>

            <div className="mt-6 p-3 rounded-xl bg-blue-50/50 border border-blue-100" data-testid="login-admin-note">
              <p className="text-xs text-blue-600 text-center">
                Tu cuenta es creada por el administrador de tu institución. Si no tienes acceso, contacta a tu administrador.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>

      <footer className="py-6 px-4 text-center">
        <p className="text-gray-400 text-sm">QHUB.BUDDY — Red Neuronal de Aprendizaje</p>
      </footer>
    </div>
  );
};

export default LoginPage;
